import React, { useEffect, useMemo, useState } from 'react'
import ListGroup from 'react-bootstrap/ListGroup'
import CenteredSpinner from 'shared/centered-spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import ActionButton from 'shared/buttons/action-button'
import Search from 'shared/forms/search'
import { useAccountCodes } from 'api/hooks/use-account-codes'
import { useAccounts } from 'api/hooks/use-accounts'
import { WHOLE_ACCOUNT_SCOPE, segmentScope } from './scopes'

const PER_PAGE = 25

// Renders a searchable, paginated checkbox list of candidate members for the
// group's scope. Selected members are pinned into the page by the API
// (AccountCodesController expands per_page by the selected ids) so a member
// stays visible even when the search moves off it.
const MemberPicker = ({
  groupType,
  accountType,
  selected,
  onChange,
  alreadyGrouped,
  selectedLabels = {},
  readOnly = false
}) => {
  // Search + page live in one params object so the shared Search component can
  // reset the page on every new query.
  const [params, setParams] = useState({ search: '', page: 1 })
  const { search, page } = params

  // Paging and search belong to the scope they were entered in. Carrying page 3
  // of the object codes over to a segment with fewer pages queried past the end
  // and rendered "No matches.", which reads as an empty segment. Reset during
  // render rather than in an effect so the stale page never reaches the query.
  const scopeKey = `${groupType}:${accountType || ''}`
  const [renderedScope, setRenderedScope] = useState(scopeKey)
  if (renderedScope !== scopeKey) {
    setRenderedScope(scopeKey)
    setParams({ search: '', page: 1 })
  }
  // Labels for selected members, so the "Selected" list can name a member that
  // isn't on the current page. Seeded from the group's saved members and topped
  // up as candidates render.
  const [labels, setLabels] = useState(selectedLabels)

  const isWholeAccount = groupType === WHOLE_ACCOUNT_SCOPE
  const scopeMeta = segmentScope(accountType)

  const codeParams = useMemo(() => {
    if (isWholeAccount || !scopeMeta) return null

    return {
      code_type: scopeMeta.codeType,
      page,
      perPage: PER_PAGE,
      [scopeMeta.searchKey]: search || undefined
      // Deliberately NOT echoing the selected ids to pin them into the page:
      // one comma-joined list of UUIDs overruns the request line (414) long
      // before AccountGroup::MAX_MEMBERS. The Selected list below covers it.
    }
  }, [isWholeAccount, scopeMeta, page, search])

  const { data: codeData, isLoading: codesLoading } = useAccountCodes({
    params: codeParams || {},
    options: { enabled: Boolean(codeParams), keepPreviousData: true }
  })

  const { data: accountData, isLoading: accountsLoading } = useAccounts({
    params: { page, perPage: PER_PAGE, search: search || undefined },
    options: { enabled: isWholeAccount, keepPreviousData: true }
  })

  const isLoading = isWholeAccount ? accountsLoading : codesLoading

  const candidates = useMemo(() => {
    if (isWholeAccount) {
      return (accountData?.data || []).map((account) => ({
        id: account.id,
        label: account.full_account,
        sublabel: account.account_description
      }))
    }

    return (codeData || []).map((code) => ({
      id: code.id,
      label: code.code,
      sublabel: code.name
    }))
  }, [isWholeAccount, accountData, codeData])

  // Remember every label we see so a selected member stays nameable after the
  // list pages away from it.
  useEffect(() => {
    if (!candidates.length) return

    setLabels((prev) => {
      const next = { ...prev }
      let changed = false
      candidates.forEach(({ id, label, sublabel }) => {
        const text = sublabel ? `${label} — ${sublabel}` : label
        if (next[id] !== text) {
          next[id] = text
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [candidates])

  // Seeded labels fill in only the ids the list hasn't taught us yet. Bails out
  // when there is nothing to add, like the effect above: `selectedLabels`
  // defaults to a fresh {} when a caller omits it, so returning a new object
  // unconditionally re-triggered this effect through its own dep and spun.
  useEffect(() => {
    setLabels((prev) => {
      const missing = Object.keys(selectedLabels).filter((id) => !(id in prev))
      if (missing.length === 0) return prev

      const next = { ...prev }
      missing.forEach((id) => {
        next[id] = selectedLabels[id]
      })
      return next
    })
  }, [selectedLabels])

  const toggle = (id) => {
    onChange(
      selected.includes(id)
        ? selected.filter((s) => s !== id)
        : [...selected, id]
    )
  }

  // Selected members are pinned to the top and never depend on being in the
  // current page of candidates; the page then contributes only what isn't already
  // selected, so nothing appears twice.
  const rows = useMemo(() => {
    const byId = new Map(
      candidates.map((candidate) => [candidate.id, candidate])
    )

    const selectedRows = selected.map((id) => {
      const candidate = byId.get(id)
      const label = candidate
        ? `${candidate.label}${candidate.sublabel ? ` — ${candidate.sublabel}` : ''}`
        : labels[id] || id

      return { id, label, selected: true, disabled: false, heldBy: null }
    })

    if (readOnly) return selectedRows

    const unselectedRows = candidates
      .filter((candidate) => !selected.includes(candidate.id))
      .map((candidate) => ({
        id: candidate.id,
        label: `${candidate.label}${candidate.sublabel ? ` — ${candidate.sublabel}` : ''}`,
        selected: false,
        // A member belongs to at most one group per scope, so a code another group
        // holds is shown but locked, naming its owner (see the exclusivity index).
        disabled: Boolean(alreadyGrouped?.[candidate.id]),
        heldBy: alreadyGrouped?.[candidate.id] || null
      }))

    return [...selectedRows, ...unselectedRows]
  }, [candidates, selected, labels, alreadyGrouped, readOnly])

  if (!isWholeAccount && !scopeMeta) {
    return (
      <p className="text-muted mb-0">Choose a segment to pick its codes.</p>
    )
  }

  return (
    <>
      {!readOnly && (
        <div className="mb-2">
          <Search
            params={params}
            setParams={setParams}
            placeholder={isWholeAccount ? 'Search accounts' : 'Search codes'}
          />
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="text-muted small">
          {selected.length} {selected.length === 1 ? 'code' : 'codes'}
          {readOnly ? '' : ' selected'}
        </span>
        {!readOnly && selected.length > 0 && (
          <ActionButton
            label="Clear all"
            variant="link"
            className="p-0"
            onClick={() => onChange([])}
          />
        )}
      </div>

      {isLoading && <CenteredSpinner />}

      {!isLoading && rows.length === 0 && (
        <p className="text-muted mb-0 small">No matches.</p>
      )}

      {/* One list, not two. Selected members come first and are always present —
          they may sit outside the current page, and this list is the only place
          they are visible — then the rest of the page's candidates. Building it
          this way means a selected code appears exactly once, which the earlier
          two-list version could not promise. */}
      {!isLoading && rows.length > 0 && (
        <ListGroup
          variant="flush"
          style={{ maxHeight: 320, overflowY: 'auto' }}
        >
          {rows.map((row) => {
            const interactive = !readOnly && !row.disabled

            return (
              <ListGroup.Item
                key={row.id}
                action={interactive}
                as={interactive ? 'button' : 'div'}
                type={interactive ? 'button' : undefined}
                disabled={row.disabled}
                aria-pressed={interactive ? row.selected : undefined}
                aria-label={
                  row.selected && !readOnly ? `Remove ${row.label}` : undefined
                }
                onClick={interactive ? () => toggle(row.id) : undefined}
                className={`d-flex align-items-center gap-2 px-2 py-1 border-0 small text-start ${
                  row.disabled ? 'text-muted' : ''
                }`}
              >
                {/* Fixed-width so labels stay aligned whether or not a row is
                    selected. */}
                <span style={{ width: 14 }} className="text-primary">
                  {row.selected && <FontAwesomeIcon icon={faCheck} />}
                </span>
                <span className={row.selected ? 'fw-semibold' : ''}>
                  {row.label}
                  {row.heldBy ? <span> (in “{row.heldBy}”)</span> : ''}
                </span>
              </ListGroup.Item>
            )
          })}
        </ListGroup>
      )}

      {!readOnly && (
        <>
          <div className="d-flex justify-content-between mt-2">
            <ActionButton
              label="Previous"
              variant="light"
              className="text-primary"
              disabled={page <= 1}
              onClick={() =>
                setParams((prev) => ({
                  ...prev,
                  page: Math.max(1, prev.page - 1)
                }))
              }
            />
            <span className="text-muted small align-self-center">
              Page {page}
            </span>
            <ActionButton
              label="Next"
              variant="light"
              className="text-primary"
              disabled={candidates.length < PER_PAGE}
              onClick={() =>
                setParams((prev) => ({ ...prev, page: prev.page + 1 }))
              }
            />
          </div>
        </>
      )}
    </>
  )
}

export default MemberPicker
